import cloneDeep from 'lodash/cloneDeep';
import { IProjectMemberView } from 'store/companies/companiesTypes';
import { ITask } from 'store/tasks/tasksType';
import { IPermission } from 'store/entity/IPermission';

export const combinePermission = (
  self: IProjectMemberView | undefined,
  roleMembers: IProjectMemberView[],
  userId: number,
  roleIds: number[]
) => {
  let ret: IProjectMemberView;
  if (self) {
    if (!self.userId) {
      ret = self as IProjectMemberView;
    } else {
      ret = cloneDeep(self);
    }
  } else {
    ret = {
      admin: false,
      canCreate: false,
      hidden: false,
      implicit: false,
      projectId: 0,
      title: undefined,
      projectRoleId: undefined,
      visibleAllTasks: false,
      userId: userId,
    };
  }
  roleMembers.forEach((roleMember) => {
    if (roleIds.includes(roleMember.roleId!)) {
      ret.admin = ret.admin || roleMember.admin;
      ret.canCreate = ret.canCreate || roleMember.canCreate;
      ret.hidden = ret.hidden && roleMember.hidden;
      ret.implicit = ret.implicit && roleMember.implicit;
      ret.projectId = ret.projectId || roleMember.projectId;
      ret.title = ret.title || roleMember.title;
      ret.projectRoleId = ret.projectRoleId || roleMember.projectRoleId;
      ret.visibleAllTasks = ret.visibleAllTasks || roleMember.visibleAllTasks;
      ret.relatedRoleId = ret.relatedRoleId || roleMember.roleId;
    }
  });

  return ret;
};

const generateUserPermission = (
  userId: number,
  task: ITask,
  canCreateInProject: boolean,
  isCompanyOwner: boolean,
  isProjectAdmin: boolean,
  isCompanyAdmin: boolean
): IPermission => {
  const permission: IPermission = {
    create: false,
    deleteOrArchive: false,
    edit: false,
    mark: false,
    markSelf: false,
    publishTask: false,
    publishSubTasks: false,
    unlock: false,
  };
  if (task) {
    const verifierIds = Object.keys(task.verifiers).map((it) => Number(it));
    const taskPublisherId = task.taskPublisher?.userId;
    const subTaskPublisherId = task.subTaskPublisher?.userId;
    const creatorId = task.creator;
    const isAssigneeAndCanEdit =
      task.assigneeCanEdit &&
      Object.keys(task.assignee).includes(userId?.toString());
    const isFollowerAndCanEdit =
      task.followerCanEdit &&
      Object.keys(task.followers).includes(userId?.toString());
    if (
      isCompanyAdmin ||
      ((verifierIds.includes(userId) || isProjectAdmin) && !task.lockProps)
    ) {
      // verifier and company owner
      permission.edit = true;
      permission.create = true;
      permission.deleteOrArchive = true;
    }
    if (userId === creatorId && !task.lockProps) {
      // creator
      permission.create = true;
    }
    if (
      !task.lockProps &&
      (userId === creatorId ||
        isAssigneeAndCanEdit ||
        isFollowerAndCanEdit ||
        isProjectAdmin)
    ) {
      permission.edit = true;
    }
    if (!verifierIds && userId === creatorId) {
      // if verifier does not exists and user is creator
      permission.deleteOrArchive = true;
    }
    permission.mark =
      isCompanyAdmin ||
      isProjectAdmin ||
      (verifierIds.includes(userId) &&
        (task.verifierCanMark || task.onlyVerifierCanMark));

    permission.markSelf =
      permission.mark ||
      (!task.onlyVerifierCanMark &&
        !task.lockMark &&
        !task.relatedTasks?.find((it) => it.blocksProgress));
    permission.publishTask =
      task.allowDirectPublish &&
      (!task.haveTaskPublisher || !!task.taskIsReadyToPublish) &&
      ((!task.onlyPublisherCanPublish && (isProjectAdmin || isCompanyOwner)) ||
        userId === taskPublisherId ||
        (!taskPublisherId &&
          !task.onlyPublisherCanPublish &&
          (verifierIds.includes(userId) ||
            (!verifierIds && userId === creatorId))));
    permission.publishSubTasks =
      task.allowPublishSubtasks &&
      (!task.haveSubTaskPublisher || !!task.subTasksAreReadyToPublish) &&
      ((!task.onlyPublisherCanPublish && (isProjectAdmin || isCompanyOwner)) ||
        userId === subTaskPublisherId ||
        (!subTaskPublisherId && permission.publishTask));
  } else {
    permission.edit = true;
  }
  permission.unlock = isCompanyOwner || isCompanyAdmin;
  if (canCreateInProject) {
    permission.create = true;
  }

  return permission;
};

export default generateUserPermission;
